import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Plus, Gauge, AlertTriangle, ArrowRight, CalendarClock } from 'lucide-react';
import { formatBillingDate } from '../lib/billing';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { SummaryGrid } from '../components/ui/SummaryGrid';
import { NovedadesCard } from '../components/dashboard/NovedadesCard';
import { PendientesSection } from '../components/dashboard/PendientesSection';
import { PrimerosPasos } from '../components/dashboard/PrimerosPasos';
import { WelcomeWizard } from '../components/onboarding/WelcomeWizard';
import { CicloInsights } from '../components/dashboard/CicloInsights';
import { UpsellSection } from '../components/dashboard/UpsellSection';
import { CollapsibleSection } from '../components/dashboard/CollapsibleSection';
import { SuperAdminDashboard } from '../components/dashboard/SuperAdminDashboard';
import { useActivePlan } from '../components/dashboard/useActivePlan';
import { supabase } from '../lib/supabase';

interface DashboardStats {
  propertiesTotal: number;
  propertiesPublished: number;
  propertiesMax: number;
  sharedProperties: number;
  sharedRequirements: number;
  activeLeads: number;
  activeRequirements: number;
}

// Super admins belong to no agency, so the agency widgets below are all zero
// for them; they get the platform dashboard. isSuperAdmin is false while
// impersonating, which keeps the agency view for that case. Split into two
// components (not an early return) so the hook count never changes mid-session.
export const DashboardPage: React.FC = () => {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin ? <SuperAdminDashboard /> : <AgencyDashboard />;
};

const AgencyDashboard: React.FC = () => {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { plan } = useActivePlan();

  const [statsLoaded, setStatsLoaded] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    propertiesTotal: 0,
    propertiesPublished: 0,
    propertiesMax: 0,
    sharedProperties: 0,
    sharedRequirements: 0,
    activeLeads: 0,
    activeRequirements: 0,
  });

  // Accurate agency-wide counts for the "Estado actual" snapshot. Head-count
  // queries + get_agency_caps (the CRM context arrays are paginated, so their
  // .length is not the agency total). Deliberately does NOT depend on those
  // arrays so pagination clicks don't re-fire the counts.
  useEffect(() => {
    if (!supabase || !profile?.id || !profile?.agency_id) return;
    let cancelled = false;

    (async () => {
      const perms = (profile as any)?.permissions;
      const isAgent = profile.role === 'agent';
      const ownFilterLeads = isAgent && !perms?.can_view_all_leads && !perms?.can_view_all_owners
        ? `agent_id.eq.${profile.id},created_by.eq.${profile.id}`
        : null;
      const ownFilterReqs = isAgent && !perms?.can_view_all_leads
        ? `agent_id.eq.${profile.id},created_by.eq.${profile.id}`
        : null;

      const propsQ = supabase.from('properties').select('id', { count: 'exact', head: true })
        .eq('agency_id', profile.agency_id).eq('is_deleted', false);

      const sharedQ = supabase.from('properties').select('id', { count: 'exact', head: true })
        .eq('agency_id', profile.agency_id).eq('is_deleted', false).eq('share_status', 'shared');

      // Same filter shape as the mobile dashboard's Requerimientos Compartidos.
      const sharedReqsQ = supabase.from('requirements').select('id', { count: 'exact', head: true })
        .eq('agency_id', profile.agency_id).eq('is_deleted', false).eq('is_shared', true);

      let leadsQ = supabase.from('contacts').select('id', { count: 'exact', head: true })
        .eq('agency_id', profile.agency_id).eq('is_deleted', false).eq('is_lead', true);
      if (ownFilterLeads) leadsQ = leadsQ.or(ownFilterLeads);

      let reqsQ = supabase.from('requirements').select('id', { count: 'exact', head: true })
        .eq('agency_id', profile.agency_id).eq('is_deleted', false).eq('status', 'active');
      if (ownFilterReqs) reqsQ = reqsQ.or(ownFilterReqs);

      const [propsRes, sharedRes, sharedReqsRes, leadsRes, reqsRes, capsRes] = await Promise.all([
        propsQ, sharedQ, sharedReqsQ, leadsQ, reqsQ, supabase.rpc('get_agency_caps'),
      ]);

      if (cancelled) return;

      const caps = (capsRes.data as any) || {};
      setStats({
        propertiesTotal: propsRes.count ?? 0,
        propertiesPublished: caps.properties_published ?? 0,
        propertiesMax: caps.max_properties ?? 0,
        sharedProperties: sharedRes.count ?? 0,
        sharedRequirements: sharedReqsRes.count ?? 0,
        activeLeads: leadsRes.count ?? 0,
        activeRequirements: reqsRes.count ?? 0,
      });
      setStatsLoaded(true);
    })();

    return () => { cancelled = true; };
  }, [profile?.id, profile?.agency_id, profile?.role]);

  // Grace-period reminder — agency users only (super_admin never sees it; the
  // RPC's payment_status is derived from the caller's own agency). 5-day grace
  // window matches get_active_plan's CASE (cycle_end + 5 days → suspended).
  // Open renewal invoice → manual-pay agencies get a "Pagar ahora" shortcut in
  // the cycle bar as soon as the daily cron generates the invoice (status 'sent'
  // — 'overdue' included so the button survives past due; grace bar takes over then).
  // Only invoices actually due: 'draft' is not sent to the client, and the due_date
  // horizon matches generate_manual_invoices' 7-day lead. Without it a stale/bootstrap
  // invoice dated months out nags (and gets paid) early — Vip Propiedades, 2026-08-10.
  const [openInvoiceId, setOpenInvoiceId] = useState<string | null>(null);
  useEffect(() => {
    if (!supabase || !profile?.agency_id) return;
    let cancelled = false;
    supabase.from('invoices').select('id')
      .eq('agency_id', profile.agency_id)
      .eq('is_recurring', true)
      .in('status', ['sent', 'overdue'])
      .lte('due_date', new Date(Date.now() + 8 * 86400_000).toISOString().slice(0, 10))
      .limit(1)
      .then(({ data }) => { if (!cancelled) setOpenInvoiceId(data?.[0]?.id ?? null); });
    return () => { cancelled = true; };
  }, [profile?.agency_id]);
  const hasOpenInvoice = !!openInvoiceId;

  const inGrace = plan?.payment_status === 'grace' && plan?.role !== 'super_admin';
  const graceDaysLeft = (() => {
    const end = plan?.membership?.cycle_end;
    if (!end) return 0;
    const deadline = new Date(end).getTime() + 5 * 86400_000;
    return Math.max(0, Math.ceil((deadline - Date.now()) / 86400_000));
  })();
  const isManualPay = !plan?.auto_pay?.active;

  return (
    <div key="dashboard-view" className="animate-page-entry">
      {/* Payment-due bar — sticky at the top of the dashboard scroll area */}
      {inGrace && (
        // -top-* mirrors <main>'s p-4/6/10 so the pinned bar sits flush with
        // the scrollport top (sticky offsets are relative to the padding box).
        <div className="sticky -top-4 sm:-top-6 lg:-top-10 z-30 mb-4">
          <div className={clsx(
            'flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 rounded-2xl border-2 shadow-lg',
            theme !== 'dark'
              ? 'bg-amber-50 border-amber-300 text-amber-900'
              : 'bg-[#2a2010] border-amber-500/40 text-amber-200'
          )}>
            <AlertTriangle size={20} className="shrink-0 text-amber-500" />
            <div className="flex-1 min-w-[200px] text-sm">
              <span className="font-bold">
                Tu membresía venció el {formatBillingDate(plan?.membership?.cycle_end, { day: 'numeric', month: 'long' })}.
              </span>{' '}
              {graceDaysLeft > 0
                ? <>Tienes <span className="font-bold">{graceDaysLeft} día{graceDaysLeft === 1 ? '' : 's'}</span> para renovar antes de la suspensión.</>
                : <>Renueva hoy para evitar la suspensión.</>}
            </div>
            {isManualPay ? (
              <button
                onClick={() => navigate(`/pagar?agency=${profile?.agency_id}`)}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-amber-950 text-sm font-bold transition-colors"
              >
                Pagar ahora <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={() => navigate('/membresia')}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-sm font-bold transition-colors"
              >
                Ver membresía <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Cycle bar — sticky, current-status agencies only (grace bar supersedes) */}
      {!inGrace && plan?.payment_status === 'current' && plan?.role !== 'super_admin' && plan?.membership?.cycle_end && (
        <div className="sticky -top-4 sm:-top-6 lg:-top-10 z-30 mb-4">
          <div className={clsx(
            'flex flex-wrap items-center gap-3 px-4 sm:px-5 py-2.5 rounded-2xl border shadow-lg text-sm',
            theme !== 'dark'
              ? 'bg-white border-slate-200 text-slate-700'
              : 'bg-slate-900 border-white/10 text-slate-300'
          )}>
            <CalendarClock size={18} className="shrink-0 text-indigo-500" />
            <div className="flex-1 min-w-[200px]">
              Tu ciclo actual termina el{' '}
              <span className="font-bold">{formatBillingDate(plan.membership.cycle_end, { day: 'numeric', month: 'long' })}</span>.
              {hasOpenInvoice && isManualPay && <> Ya tienes una factura pendiente de pago.</>}
            </div>
            {hasOpenInvoice && isManualPay && (
              <button
                /* Carry the invoice so /pagar settles THIS invoice instead of
                   building a generic renewal that stacks a cycle on the anchor. */
                onClick={() => navigate(`/pagar?agency=${profile?.agency_id}&invoice=${openInvoiceId}`)}
                className="shrink-0 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold transition-colors"
              >
                Pagar ahora <ArrowRight size={15} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* First-login wizard — self-gates on onboarding_completed_at */}
      <WelcomeWizard theme={theme} />

      {/* Welcome Header */}
      <div className="stagger-item flex justify-between items-end mb-6" style={{ '--i': 0 } as React.CSSProperties}>
        <div>
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-black tracking-tight text-primary mb-1">
            Bienvenido, {profile?.full_name || 'Usuario'}.
          </h3>
          <p className="text-sm lg:text-base text-secondary">Esto es lo que está pasando con tu portafolio hoy.</p>
        </div>
        <button
          onClick={() => navigate('/propiedades/nueva')}
          className="hidden sm:flex bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-xl font-bold shadow-lg shadow-indigo-600/20 transition-all items-center gap-2"
        >
          <Plus size={20} /> Nueva Propiedad
        </button>
      </div>

      {/* 1. Novedades (collapsible news / vlog) */}
      <NovedadesCard theme={theme} staggerIndex={1} />

      {/* 2. Pendientes de hoy (actionable to-dos) */}
      <PendientesSection theme={theme} staggerIndex={2} />

      {/* 2b. Herramientas clave — tools checklist + badge; self-fetches per-user
          and self-hides once the badge is earned + celebrated */}
      {profile?.agency_id && <PrimerosPasos theme={theme} />}

      {/* 3. Estado actual — live snapshot */}
      <CollapsibleSection title="Estado actual" icon={Gauge} storageKey="dash_estado" theme={theme} staggerIndex={3}>
        <SummaryGrid
          theme={theme}
          propertiesPublished={stats.propertiesPublished}
          propertiesMax={stats.propertiesMax}
          propertiesTotal={stats.propertiesTotal}
          sharedProperties={stats.sharedProperties}
          sharedRequirements={stats.sharedRequirements}
          activeLeads={stats.activeLeads}
          activeRequirements={stats.activeRequirements}
        />
      </CollapsibleSection>

      {/* 4. Tu ciclo en números (this vs previous cycle) */}
      <CicloInsights plan={plan} theme={theme} staggerIndex={4} />

      {/* 5. Potencia tu cuenta (contextual add-ons) */}
      <UpsellSection plan={plan} theme={theme} staggerIndex={5} />
    </div>
  );
};
